'use client'
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import { GlobalTypography } from 'cms-types'
import { MouseEvent, SyntheticEvent, useCallback, useEffect, useRef, useState } from 'react'
import { Address } from 'shopify-types'

import { useWindowSize } from '../../hooks/useWindowSize'
import { Button, Checkbox, Icon, Input } from '../../index'
import { ShopifyResponseAddress } from '../../types'

type LanguageModalProps = {
  showModal: boolean
  onClose: () => void
  address: Address | null
  onEdit: (address: Address, makeDefault: boolean) => Promise<void>
  typography: GlobalTypography['authSection']
  isDefaultAddress: boolean
}

const BLANK_ADDRESS = {
  id: '',
  address1: '',
  address2: '',
  city: '',
  company: '',
  country: '',
  firstName: '',
  lastName: '',
  phone: '',
  province: '',
  zip: '',
}

function AddressEditModal({
  onClose,
  showModal,
  address,
  onEdit,
  typography,
  isDefaultAddress,
}: LanguageModalProps) {
  const { addressesTab } = typography
  const modalRef = useRef<HTMLDialogElement>(null)
  const [closeAnimationPlaying, setCloseAnimationPlaying] = useState(false)
  const [editAddress, setEditAddress] = useState<Address>(address || BLANK_ADDRESS)
  const [isDefault, setIsDefault] = useState(isDefaultAddress)
  const [fieldErrors, setFieldErrors] = useState<Address>(BLANK_ADDRESS)
  const [isLoading, setIsLoading] = useState(false)
  const { isDesktop } = useWindowSize()

  useEffect(() => {
    if (showModal && modalRef.current !== null) {
      modalRef.current.showModal()
      modalRef.current.classList.add(isDesktop ? 'openDownToUp' : 'openRightToLeft')
    }
  }, [isDesktop, showModal])

  const onModalCloseClick = useCallback(() => {
    modalRef?.current?.classList.add(isDesktop ? 'closeDownToUp' : 'closeRightToLeft')

    setCloseAnimationPlaying(true)
  }, [isDesktop])

  const onEscapeClose = useCallback(
    (e: SyntheticEvent<HTMLDialogElement>) => {
      e.preventDefault()
      onModalCloseClick()
    },
    [onModalCloseClick],
  )

  const onModalClick = useCallback(
    (event: MouseEvent<HTMLDialogElement>) => {
      if (!modalRef.current) return

      // check if we click on backdrop
      const rect = modalRef.current.getBoundingClientRect()

      if (
        event.clientX < rect.left ||
        event.clientX > rect.right ||
        event.clientY < rect.top ||
        event.clientY > rect.bottom
      ) {
        onModalCloseClick()
      }
    },
    [onModalCloseClick],
  )

  const onCloseAnimationEnd = useCallback(() => {
    if (!modalRef.current || !closeAnimationPlaying) return

    modalRef.current.close()
    setCloseAnimationPlaying(false)

    onClose()
  }, [closeAnimationPlaying, onClose])

  const editField = (fieldName: string, value: string) => {
    setEditAddress(prev => ({ ...prev, [fieldName]: value }))
  }

  const onSubmit = async () => {
    const errors: ShopifyResponseAddress = { ...BLANK_ADDRESS }

    if (!editAddress.country) {
      errors.country = `${addressesTab.countryText} ${typography.isRequiredText}`
    }

    if (!editAddress.city) {
      errors.city = `${addressesTab.cityText} ${typography.isRequiredText}`
    }

    if (!editAddress.province) {
      errors.province = `${addressesTab.provinceText} ${typography.isRequiredText}`
    }

    if (!editAddress.zip) {
      errors.zip = `${addressesTab.zipCodeText} ${typography.isRequiredText}`
    }

    if (!editAddress.address1) {
      errors.address1 = `${addressesTab.addressLine1Text} ${typography.isRequiredText}`
    }

    if (!editAddress.firstName) {
      errors.firstName = `${typography.firstNameText} ${typography.isRequiredText}`
    }

    if (!editAddress.lastName) {
      errors.lastName = `${typography.lastNameText} ${typography.isRequiredText}`
    }

    if (!editAddress.phone) {
      errors.phone = `${addressesTab.phoneNumberText} ${typography.isRequiredText}`
    }

    setFieldErrors(errors)

    if (Object.values(errors).every(value => !value)) {
      setIsLoading(true)
      await onEdit(editAddress, isDefault)
      setIsLoading(false)
    }
  }

  return (
    <dialog
      ref={modalRef}
      onAnimationEnd={onCloseAnimationEnd}
      className='fixed m-auto h-full max-h-full w-full max-w-full bg-lightgray backdrop:bg-[#00000080] xl:m-auto xl:h-fit xl:max-h-[90%] xl:w-[70%] xl:max-w-[90%]'
      onClick={onModalClick}
      onCancel={onEscapeClose}
    >
      <div className='relative flex flex-col rounded-md bg-lightgray p-5 text-left xl:p-10'>
        <button className='absolute right-6 top-6 ml-auto' onClick={onModalCloseClick} autoFocus>
          <Icon name='MdClose' size={20} />
        </button>

        <h5 className='mb-4 mt-4'>Edit address</h5>

        <div className='grid grid-cols-1 gap-x-8 gap-y-2 xl:grid-cols-2'>
          <Input
            label={addressesTab.countryText}
            value={editAddress.country}
            onChange={text => editField('country', text)}
            type='text'
            errorMessage={fieldErrors.country}
          />
          <Input
            label={addressesTab.provinceText}
            value={editAddress.province}
            onChange={text => editField('province', text)}
            type='text'
            errorMessage={fieldErrors.province}
          />
          <Input
            label={addressesTab.cityText}
            value={editAddress.city}
            onChange={text => editField('city', text)}
            type='text'
            errorMessage={fieldErrors.city}
          />
          <Input
            label={addressesTab.zipCodeText}
            value={editAddress.zip}
            onChange={text => editField('zip', text)}
            type='text'
            errorMessage={fieldErrors.zip}
          />
          <Input
            label={addressesTab.addressLine1Text}
            value={editAddress.address1}
            onChange={text => editField('address1', text)}
            type='text'
            errorMessage={fieldErrors.address1}
          />
          <Input
            label={addressesTab.addressLine2Text}
            value={editAddress.address2}
            onChange={text => editField('address2', text)}
            type='text'
            errorMessage={fieldErrors.address2}
          />
          <Input
            label={typography.firstNameText}
            value={editAddress.firstName}
            onChange={text => editField('firstName', text)}
            type='text'
            errorMessage={fieldErrors.firstName}
          />
          <Input
            label={typography.lastNameText}
            value={editAddress.lastName}
            onChange={text => editField('lastName', text)}
            type='text'
            errorMessage={fieldErrors.lastName}
          />
          <Input
            label={addressesTab.phoneNumberText}
            value={editAddress.phone}
            onChange={text => editField('phone', text)}
            type='text'
            errorMessage={fieldErrors.phone}
          />
          <Input
            label={addressesTab.companyText}
            value={editAddress.company}
            onChange={text => editField('company', text)}
            type='text'
            errorMessage={fieldErrors.company}
          />
          <Checkbox
            text={addressesTab.makeDefaultAddressText}
            checked={isDefault}
            onChange={() => setIsDefault(prev => !prev)}
          />

          <Button
            appearance='dark'
            onPress={onSubmit}
            type='submit'
            className='mt-2 !max-w-full rounded-pill xl:col-span-2'
            isDisabled={isLoading}
            isLoading={isLoading}
          >
            {typography.submitButtonText}
          </Button>
        </div>
      </div>
    </dialog>
  )
}

export default AddressEditModal
